/**
 * Frontend mirror of orchestrator/src/artifacts/docImages.ts — resolves a doc
 * page's relative image reference (`<img src>` or `![]()`) to the base64 image
 * artifact that holds its bytes, so the UI can render it inline as a `data:` URI.
 *
 * Kept dependency-free (no node:path) with small POSIX-path helpers so it runs
 * in the browser bundle. Logic must stay in sync with the backend module.
 */

import type { Artifact } from '../types';

/* ------------------------------ path helpers ----------------------------- */

function normPath(p: string): string {
  const parts = p.replace(/\\/g, '/').split('/');
  const out: string[] = [];
  for (const seg of parts) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (out.length && out[out.length - 1] !== '..') out.pop();
      else out.push('..');
    } else {
      out.push(seg);
    }
  }
  return out.join('/');
}

function dirnameOf(p: string): string {
  const n = normPath(p);
  const i = n.lastIndexOf('/');
  return i === -1 ? '' : n.slice(0, i);
}

function basenameOf(p: string): string {
  const n = normPath(p);
  const i = n.lastIndexOf('/');
  return i === -1 ? n : n.slice(i + 1);
}

/* ------------------------------ artifacts --------------------------------- */

export function isImageArtifact(a: Artifact): boolean {
  return a.encoding === 'base64' && !!a.mimeType && a.mimeType.startsWith('image/');
}

export function isExternalRef(src: string): boolean {
  return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(src) || src.startsWith('data:') || src.startsWith('#');
}

export interface ImageRef {
  raw: string;
  src: string;
  alt?: string;
}

const IMG_TAG_RE = /<img\b[^>]*>/gi;
const SRC_ATTR_RE = /\bsrc\s*=\s*["']([^"']+)["']/i;
const ALT_ATTR_RE = /\balt\s*=\s*["']([^"']*)["']/i;
const MD_IMG_RE = /!\[([^\]]*)\]\(\s*([^)\s]+)(?:\s+[^)]*)?\)/g;

/**
 * Char ranges covered by fenced (``` / ~~~) or inline (`` ` ``) code spans, so
 * example image syntax shown inside code isn't treated as a live reference.
 */
function codeRanges(md: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  const fence = /(^|\n)[ \t]*(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:\n[ \t]*\2[ \t]*(?=\n|$)|$)/g;
  let m: RegExpExecArray | null;
  while ((m = fence.exec(md)) !== null) ranges.push([m.index, m.index + m[0].length]);
  const inline = /`[^`\n]+`/g;
  while ((m = inline.exec(md)) !== null) {
    const idx = m.index;
    if (!ranges.some(([s, e]) => idx >= s && idx < e)) ranges.push([idx, idx + m[0].length]);
  }
  return ranges;
}

function inCode(idx: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([s, e]) => idx >= s && idx < e);
}

export function listImageRefs(markdown: string): ImageRef[] {
  const out: ImageRef[] = [];
  if (!markdown) return out;
  const ranges = codeRanges(markdown);
  let m: RegExpExecArray | null;
  IMG_TAG_RE.lastIndex = 0;
  while ((m = IMG_TAG_RE.exec(markdown)) !== null) {
    if (inCode(m.index, ranges)) continue;
    const tag = m[0];
    const src = SRC_ATTR_RE.exec(tag)?.[1];
    if (!src) continue;
    out.push({ raw: tag, src, alt: ALT_ATTR_RE.exec(tag)?.[1] });
  }
  MD_IMG_RE.lastIndex = 0;
  while ((m = MD_IMG_RE.exec(markdown)) !== null) {
    if (inCode(m.index, ranges)) continue;
    out.push({ raw: m[0], src: m[2], alt: m[1] || undefined });
  }
  return out;
}

export interface ImageIndex {
  byPath: Map<string, Artifact>;
  byBase: Map<string, Artifact[]>;
}

export function buildImageIndex(artifacts: Artifact[]): ImageIndex {
  const byPath = new Map<string, Artifact>();
  const byBase = new Map<string, Artifact[]>();
  for (const a of artifacts) {
    if (!isImageArtifact(a)) continue;
    const norm = normPath(a.name);
    byPath.set(norm, a);
    const base = basenameOf(norm);
    const arr = byBase.get(base) ?? [];
    arr.push(a);
    byBase.set(base, arr);
  }
  return { byPath, byBase };
}

export function resolveImageRef(
  pageName: string,
  src: string,
  index: ImageIndex,
): Artifact | undefined {
  if (isExternalRef(src)) return undefined;
  const pageDir = dirnameOf(pageName);
  const joined = normPath(pageDir ? `${pageDir}/${src}` : src);
  const direct = index.byPath.get(joined);
  if (direct) return direct;
  const rootHit = index.byPath.get(normPath(src));
  if (rootHit) return rootHit;
  const candidates = index.byBase.get(basenameOf(joined));
  if (candidates && candidates.length === 1) return candidates[0];
  return undefined;
}

/* ------------------------- project-wide helpers --------------------------- */

interface StepLike {
  key: string;
  artifacts?: Artifact[];
}
interface PhaseLike {
  order?: number;
  steps?: StepLike[];
}
export interface ProjectLike {
  phases?: PhaseLike[];
}

/** Every artifact across every step of a project (flat). */
export function collectAllArtifacts(project: ProjectLike): Artifact[] {
  const out: Artifact[] = [];
  for (const ph of project.phases ?? []) {
    for (const st of ph.steps ?? []) {
      for (const a of st.artifacts ?? []) out.push(a);
    }
  }
  return out;
}

/** Project-wide image index (used to resolve any page's refs). */
export function buildProjectImageIndex(project: ProjectLike): ImageIndex {
  return buildImageIndex(collectAllArtifacts(project));
}

/**
 * A step's artifacts with each markdown page's content overlaid by the latest
 * same-named `.md` page produced at-or-after this step (in phase order). This is
 * what lets the `product-docs` viewer show the placeholder-injected pages that
 * the downstream `docs-screenshots` step actually produced.
 */
export function overlayStepArtifacts(project: ProjectLike, stepKey: string): Artifact[] {
  const flat = [...(project.phases ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap((p) => p.steps ?? []);
  const ownerIdx = flat.findIndex((s) => s.key === stepKey);
  if (ownerIdx < 0) return [];
  const ownerArts = flat[ownerIdx].artifacts ?? [];

  const latest = new Map<string, Artifact>();
  for (let i = ownerIdx; i < flat.length; i++) {
    for (const a of flat[i].artifacts ?? []) {
      if (a.name.toLowerCase().endsWith('.md')) latest.set(normPath(a.name), a);
    }
  }

  return ownerArts.map((a) => {
    if (!a.name.toLowerCase().endsWith('.md')) return a;
    const inj = latest.get(normPath(a.name));
    return inj && inj.content !== a.content ? { ...a, content: inj.content } : a;
  });
}

/** A `data:` URI for an image artifact. */
export function imageDataUri(a: Artifact): string {
  return `data:${a.mimeType || 'image/png'};base64,${a.content}`;
}

/**
 * Rewrite a page's image refs (relative `<img src>` and `![]()`) to inline
 * `data:` URIs sourced from the project's image artifacts. Refs that don't
 * resolve are left untouched. Returns the rewritten markdown/HTML string.
 */
export function inlineDocImages(pageName: string, content: string, index: ImageIndex): string {
  if (!content) return content;
  let out = content;

  // HTML <img> tags: replace the src attribute value (skip refs inside code).
  let ranges = codeRanges(out);
  out = out.replace(IMG_TAG_RE, (tag: string, offset: number) => {
    if (inCode(offset, ranges)) return tag;
    const src = SRC_ATTR_RE.exec(tag)?.[1];
    if (!src) return tag;
    const art = resolveImageRef(pageName, src, index);
    if (!art) return tag;
    return tag.replace(SRC_ATTR_RE, `src="${imageDataUri(art)}"`);
  });

  // Markdown images: ![alt](src) -> ![alt](data:...). Recompute ranges since the
  // <img> pass above may have shifted offsets.
  ranges = codeRanges(out);
  out = out.replace(MD_IMG_RE, (raw: string, alt: string, src: string, offset: number) => {
    if (inCode(offset, ranges)) return raw;
    const art = resolveImageRef(pageName, src, index);
    if (!art) return raw;
    return `![${alt}](${imageDataUri(art)})`;
  });

  return out;
}
